/*
    Компонент для вывода информации по адресу
    Обогащает и форматирует полученные в props данные с помощью domain/address,
    по готовности данных выполняет readyCallback, чтобы сообщить родителю о готовности

    Меняет отображение и часть логики в зависимости от того, где выводится
    (suggestion или clean, granular)

    Заново запускает обогащение данных при их изменении.
    Раскрывает для родительского компонента свои данные validationMessage и isGood
    (используются в демо-форме гранулярных подсказок по адресам)
*/

import helper from "../../../domain/address";
import SuggestionsService from "../../service";
import objectLink from "../../object-link";
import utils from "../../../utils";
import modal from "../../../modal";

const deliveryService = new SuggestionsService("delivery");

const template = /* html */`
    <div class="flex-column gap-2">
        <div
            :class="{'has-success': isGood, 'has-error': !isGood}"
            class="feedback_wrapper"
            v-if="validationMessage && type === 'suggestion' && !granular"
        >
            <span
                data-ref="feedback"
                class="feedback"
                >
                    <i
                        class="fa fa-lg"
                        :class="[isGood?'fa-check-circle':'fa-exclamation-circle']"
                ></i> [[ validationMessage ]]
            </span>
        </div>
        <div class="flex-column items-start flex-row-sm gap-2 gap-sm-4 max-w-12">
            <div
                class="grid results-block results-block--left items-start gap-sm-1"
                v-if="!granular"
            >
                <span
                    class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
                >
                    [[ type === "suggestion" ? 'Адрес по частям' : 'Результат разбора' ]]
                </span>
                <span class="color-gray">Страна</span>
                <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">[[ address.country || '—' ]]</span>
                <span class="color-gray index-key">Индекс</span>
                <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0 index-value">
                    <span data-ref="postal_code">
                        [[ address.postal_code || '—']]
                    </span>
                    <span
                        v-if="!isForeign && address.postal_code && showPostDate"
                        class="color-gray block"
                    >по данным Почты от [[ postDate ]]</span>
                </div>
                <template v-if="!isForeign">
                    <span class="color-gray">Фед. округ</span>
                    <span data-ref="federal-district" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                        [[ address.federal_district || '—' ]]
                    </span>
                </template>
                <template v-if="region">
                    <span class="color-gray">
                        Регион
                    </span>
                    <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                        <span data-ref="region">
                            [[ region ]]
                            <a
                                v-if="isRegionCity"
                                class="link-nondecorated nx-link js-modal"
                                href="#region-info"
                            >
                                <i class="fa fa-question-circle" />
                            </a>
                        </span>
                        <span v-if="hasHistoryValues('region')" class="color-gray block">
                            [[ historyValues ]]
                        </span>
                    </div>
                </template>
                <span class="color-gray">Район</span>
                <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                    <span data-ref="area">[[ areaWithSubarea || '—' ]]</span>
                    <span v-if="centralArea" class="color-gray block" data-ref="central-area">
                        [[ centralArea ]]
                    </span>
                    <span
                        v-if="hasHistoryValues('area')"
                        class="color-gray block"
                    >
                        [[ historyValues ]]
                    </span>
                </div>
                <span class="color-gray">Город / н.п.</span>
                <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                    <span data-ref="city">[[ cityWithSettlement || '—' ]]</span>
                    <span v-if="capitalMarker" class="color-gray block">
                        [[ capitalMarker ]]
                    </span>
                    <span
                        v-if="hasHistoryValues('city')"
                        class="color-gray block"
                        data-ref="city-history"
                    >
                        [[ historyValues ]]
                    </span>
                </div>
                <span class="color-gray">Улица</span>
                <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                    <span data-ref="street">[[ address.street_with_type || '—' ]]</span>
                    <span
                        v-html="cityAdditional"
                        class="color-gray block"
                        data-ref="city-additional"
                    />
                    <span
                        v-if="hasHistoryValues('street')"
                        class="color-gray block"
                    >
                        [[ historyValues ]]
                    </span>
                </div>
                <template v-if="!address.stead">
                    <span class="color-gray">Дом</span>
                    <span data-ref="house" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                        [[ houseWithBlock || '—' ]]
                    </span>
                    <template v-if="type === 'clean'">
                        <span class="color-gray">Подъезд</span>
                        <span data-ref="house" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ entrance || '—' ]]
                        </span>
                        <span class="color-gray">Этаж</span>
                        <span data-ref="house" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ floor || '—' ]]
                        </span>
                    </template>
                    <span class="color-gray">Квартира</span>
                    <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                        <span data-ref="flat">[[ address.flat || '—' ]]</span>
                        <div v-if="address.flat_area" data-ref="flat-additional" class="color-gray">
                            площадь [[ address.flat_area ]] м²[[ address.flat_price ? ',' : '' ]]
                            <span v-if="address.flat_price" class="block">
                                стоимость [[ flatPrice ]]
                            </span>
                        </div>
                    </div>
                    <template v-if="address.room">
                        <span class="color-gray">Комната</span>
                        <span data-ref="room" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.room ]]
                        </span>
                    </template>
                </template>
                <template v-if="address.stead">
                    <span class="color-gray">Зем. участок</span>
                    <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                        <span data-ref="stead">[[ address.stead ]]</span>
                        <span
                            v-if="address.stead_cadnum"
                            data-ref="stead-cadnum"
                            class="color-gray block"
                        >
                            кадастровый номер [[ address.stead_cadnum ]]
                        </span>
                    </div>
                </template>
                <div
                    class="copy-link grid-col-span-sm-2
                        flex gap-0_25 items-baseline max-w-fit nx-link"
                    v-if="copyLinkEnabled"
                    @click="copyLink"
                >
                    <i class="fa fa-link"></i>
                    <div>
                        <span class="nx-link nx-link--dashed" data-ref="copy-link">
                            Копировать ссылку на адрес
                        </span>
                        <span class="copied" v-if="copied"> ✓ </span>
                    </div>
                </div>
            </div>
            <div class="grid results-block items-start gap-sm-1">
                <span
                    class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
                >
                    Дополнительная информация
                </span>
                <template v-if="isForeign">
                    <template v-if="address.fias_id && !address.geoname_id">
                        <span class="color-gray">OpenStreetMap ID</span>
                        <span data-ref="fias-id" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.fias_id ]]
                        </span>
                    </template>
                    <template v-if="address.geoname_id">
                        <span class="color-gray">Geoname ID</span>
                        <span data-ref="geoname-id" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.geoname_id ]]
                        </span>
                    </template>
                </template>
                <template v-if="isForeign || isValidFiasLevel">
                    <template v-if="address.geo_lat">
                        <span class="color-gray">Геокоординаты</span>
                        <span data-ref="geo" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            <a
                                :href="geoLink"
                                data-ref="geo-link"
                                title="открыть карту"
                                target="_blank"
                            >[[ geoLinkLabel ]]</a>
                            <span class="geolink-description">[[' ' + geoLinkDescription]]</span>
                        </span>
                    </template>
                    <template v-if="address.timezone">
                        <span class="color-gray">Часовой пояс</span>
                        <span data-ref="timezone" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.timezone ]]
                        </span>
                    </template>
                    <template v-if="isoCodes">
                        <span class="color-gray">ISO-код</span>
                        <span data-ref="iso-codes" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ isoCodes ]]
                        </span>
                    </template>
                </template>
                <template v-if="isValidFiasLevel">
                    <span class="color-gray fias-key">Уровень по ФИАС</span>
                    <span data-ref="fias-level" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0 fias-value">
                        [[ fiasLevel ]]
                    </span>
                    <template v-if="cadNum">
                        <span class="color-gray cadNum-key">Кадастр. номер</span>
                        <span data-ref="cad-num" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0 cadNum-value">
                            [[ cadNum ]]
                        </span>
                    </template>
                    <template v-if="fiasCodes">
                        <span class="color-gray">Код ФИАС (ГАР)</span>
                        <span data-ref="fias-codes" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ fiasCodes ]]
                        </span>
                    </template>
                    <template v-if="address.kladr_id">
                        <span class="color-gray">Код КЛАДР</span>
                        <span data-ref="kladr-id" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.kladr_id ]]
                        </span>
                    </template>
                    <template v-if="address.okato">
                        <span class="color-gray">Код ОКАТО</span>
                        <span data-ref="okato" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.okato ]]
                        </span>
                    </template>
                    <template v-if="address.oktmo">
                        <span class="color-gray">Код ОКТМО</span>
                        <span data-ref="oktmo" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.oktmo ]]
                        </span>
                    </template>
                    <template v-if="address.tax_office">
                        <span class="color-gray">Код ИФНС</span>
                        <span data-ref="tax-office" class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                            [[ address.tax_office ]]
                        </span>
                    </template>
                    <template v-if="metro">
                        <span class="color-gray">Ближайшее метро</span>
                        <div
                            data-ref="metro"
                            class="mb-1 mb-sm-0 mt-0_25 mt-sm-0 address-metro"
                            v-html="metro"
                        />
                    </template>
                    <template v-if="delivery">
                        <span class="color-gray mb-0_25 mb-sm-0 max-w-sm-min">
                            Идентификаторы города в службах доставки
                        </span>
                        <div
                            data-ref="delivery"
                            class="flex-column gap-0_25 gap-sm-0_5"
                        >
                            <span data-ref="cdek-id">СДЭК — [[ delivery.cdek_id ]]</span>
                            <span data-ref="boxberry-id">
                                Boxberry — [[ delivery.boxberry_id ]]
                            </span>
                            <span data-ref="dpd-id">DPD — [[ delivery.dpd_id ]]</span>
                            <span>
                                ID определяем отдельно
                                <span class="block">
                                    через <a href="/api/delivery/">
                                        API «Город в службе доставки»
                                    </a>
                                </span>
                            </span>
                        </div>
                    </template>
                </template>
            </div>
        </div>
        <teleport to='body'>
            <div class="modal-container" id="region-info">
                <div class="modal default-modal">
                    <a href="#close" class="modal__close" data-action="close">&times;</a>
                    <p>
                        <template v-if="type === 'clean'">
                            «Стандартизация» строго следует ГАР (ФИАС) — адресному 
                            справочнику налоговой.
                        </template>
                        <template v-else>
                            «Подсказки» по адресам в муниципальном делении строго 
                            следуют ГАР — адресному справочнику налоговой. 
                        </template>
                        Там Москва, Санкт-Петербург, Севастополь и Байконур — регионы, а не города. 
                        Поэтому сервис возвращает названия этих населенных 
                        пунктов в поле «Регион».
                    </p>
                </div>
            </div>
        </teleport>
    </div>
`;

const computedFields = Object.keys(helper)
    .filter(key => key !== "hasHistoryValues");

export default {
    name: "AddressResults",
    template,
    props: {
        suggestion: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: "suggestion",
            validator(value) {
                return ["suggestion", "clean"].includes(value);
            },
        },
        granular: {
            type: Boolean,
            default: false,
        },
        readyCallback: {
            type: Function,
            default: () => {},
        },
        showPostDate: {
            type: Boolean,
            default: true,
        },
    },
    inject: ["postDate"],
    data() {
        return {
            copied: false,
            delivery: null,
            ...computedFields
                .reduce((acc, cur) => {
                    acc[cur] = "";
                    return acc;
                }, {}),
        };
    },
    mounted() {
        this.getComputedData();
        this.getDelivery()
            .then(() => {
                this.readyCallback();
            });
        setTimeout(() => {
            modal.attach();
        });
    },
    computed: {

        address() {
            return this.suggestion;
        },

        copyLinkEnabled() {
            if (helper.isForeign(this.suggestion)) {
                return Boolean(this.suggestion.fias_id || this.suggestion.geoname_id);
            }
            return this.suggestion.fias_id && this.suggestion.fias_actuality_state === "0";
        },

        historyValues() {
            if (!this.suggestion.history_values) return "";
            return `бывш. ${utils.join(this.suggestion.history_values, ", ")}`;
        },
    },
    watch: {
        suggestion: {
            handler() {
                this.getComputedData();
                this.getDelivery()
                    .then(() => {
                        this.readyCallback();
                        setTimeout(() => {
                            modal.attach();
                        });
                    });
            },
            deep: true,
        },
    },
    methods: {
        getComputedData() {
            return computedFields
                .reduce((acc, cur) => {
                    acc[cur] = helper[cur](this.suggestion);
                    return acc;
                }, this);
        },
        hasHistoryValues(level) {
            return helper.hasHistoryValues(this.suggestion, level);
        },
        getDelivery() {
            if (this.type === "clean" && this.suggestion.delivery) {
                // для формы стандартизации в suggestion может быть
                // закеширован дефолтный результат, делать запрос не нужно
                this.delivery = this.suggestion.delivery;
                return Promise.resolve();
            }

            const cityKladrId = this.suggestion.settlement_kladr_id ||
                                this.suggestion.city_kladr_id ||
                                this.suggestion.region_kladr_id;
            this.delivery = null;
            return new Promise(resolve => {
                deliveryService.findById(cityKladrId)
                    .done(suggestion => {
                        const {
                            boxberry_id: boxberry, cdek_id: cdek, dpd_id: dpd,
                        } = suggestion.data;

                        if (boxberry || cdek || dpd) {
                            this.delivery = {
                                boxberry_id: boxberry || "нет данных",
                                cdek_id: cdek || "нет данных",
                                dpd_id: dpd || "нет данных",

                            };
                        }
                        resolve();
                    })
                    .fail(() => {
                        resolve();
                    });
            });
        },
        copyLink(event) {
            const addressId = this.suggestion.fias_id || this.suggestion.geoname_id;
            const url = objectLink.buildUrl("address", addressId);
            objectLink.copy(url);
            objectLink.showCopied(this);
            event.preventDefault();
        },
    },
    expose: ["isGood", "validationMessage", "cadNum"],
};
